import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';

export function PresenterView({ api, eventId, eventStateId }) {
    const [eventStatus, setEventStatus] = useState(null);
    const [inputTime, setInputTime] = useState('');
    const [inputPenalties, setInputPenalties] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [wsConnected, setWsConnected] = useState(false);
    const { eventId: urlEventId } = useParams();
    const currentEventId = eventId || urlEventId;

    // Fetch initial event status and set up WebSocket connection
    useEffect(() => {
        if (!eventStateId) return;

        let ws;
        const connectWebSocket = () => {
            // Use the API's WebSocket connection method
            ws = api.connectToEventState(eventStateId);

            ws.onopen = () => {
                console.log('WebSocket connected');
                setWsConnected(true);
            };

            ws.onmessage = (event) => {
                const data = JSON.parse(event.data);
                if (data.type === 'status') {
                    setEventStatus(data.data);
                }
            };

            ws.onclose = () => {
                console.log('WebSocket disconnected');
                setWsConnected(false);
                // Attempt to reconnect after a delay
                setTimeout(connectWebSocket, 3000);
            };

            ws.onerror = (error) => {
                console.error('WebSocket error:', error);
                ws.close();
            };
        };

        // Initial fetch of event status
        const fetchStatus = async () => {
            try {
                const status = await api.getEventStatus(eventStateId);
                setEventStatus(status);
            } catch (err) {
                setError('Failed to fetch event status');
            }
        };

        fetchStatus();
        connectWebSocket();

        return () => {
            if (ws) {
                ws.close();
            }
        };
    }, [api, eventStateId]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!inputTime) return;
        setSubmitting(true);
        setError(null);

        try {
            // Use the same completeRun method as ProducerView
            await api.completeRun(eventStateId, {
                participantId: eventStatus.currentParticipant.id,
                time: parseFloat(inputTime),
                penalties: inputPenalties ? parseFloat(inputPenalties) : 0,
                horseName: eventStatus.currentParticipant.horses[0] || 'Unknown'
            });

            setInputTime('');
            setInputPenalties('');
            // No need to manually refetch - WebSocket will handle the update
        } catch (err) {
            setError('Failed to record run');
            console.error('Error recording run:', err);
        } finally {
            setSubmitting(false);
        }
    };

    const handleNextParticipant = async () => {
        setSubmitting(true);
        setError(null);

        try {
            await api.startNextParticipant(eventStateId);
            // No need to manually refetch - WebSocket will handle the update
        } catch (err) {
            setError('Failed to advance to next participant');
            console.error('Error advancing participant:', err);
        } finally {
            setSubmitting(false);
        }
    };

    const renderRun = (run) => {
        if (run.type === 'drag') {
            return (
                <div key={run.id} className="flex items-center justify-center p-6 bg-gray-100 rounded-lg shadow-sm">
                    <div className="flex items-center space-x-3">
                        <span className="text-2xl">🚜</span>
                        <div className="text-center">
                            <h2 className="text-2xl font-bold text-gray-800">Arena Drag</h2>
                            <p className="text-gray-600">Preparing arena for next set of runs</p>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div key={run.id} className="grid grid-cols-3 gap-4 p-6 bg-white rounded-lg shadow-sm">
                <div>
                    <h3 className="text-lg font-semibold">{run.participantName}</h3>
                    <p className="text-gray-600">{run.horseName}</p>
                </div>
                <div className="text-center">
                    <div className="text-3xl font-bold">
                        {run.time ? run.time.toFixed(3) : '--'}
                    </div>
                    {run.penalties > 0 && (
                        <div className="text-xl text-red-500">+{run.penalties}</div>
                    )}
                </div>
                <div className="text-right">
                    <div className="text-lg font-medium">{run.division || '--'}</div>
                    <div className="text-gray-600">{run.placing || '--'}</div>
                </div>
            </div>
        );
    };

    // Calculate runs until next drag
    const calculateRunsUntilDrag = (eventStatus) => {
        if (!eventStatus?.currentClass?.dragsEveryNRuns) return null;
        const dragsEveryNRuns = eventStatus.currentClass.dragsEveryNRuns;
        if (dragsEveryNRuns <= 0) return null;

        const runs = eventStatus.runs || [];
        const currentRunCount = runs.length;
        const runsUntilDrag = dragsEveryNRuns - (currentRunCount % dragsEveryNRuns);
        return runsUntilDrag;
    };

    const renderCurrentParticipant = (participant) => {
        if (!participant) return null;

        // Special rendering for drag
        if (participant.type === 'drag') {
            return (
                <>
                    <div className="flex items-center justify-center space-x-2 mb-4">
                        <div className="h-8 w-8 rounded-full bg-orange-500/10 flex items-center justify-center">
                            <span className="text-orange-600 text-lg">🚜</span>
                        </div>
                        <h4 className="text-xl font-bold text-foreground">Arena Drag</h4>
                    </div>
                    <p className="text-lg text-muted-foreground mb-4">Preparing arena for next set of runs</p>
                    <button
                        onClick={handleNextParticipant}
                        disabled={submitting}
                        className="inline-flex w-full items-center justify-center rounded-md text-lg font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-orange-600 text-white hover:bg-orange-700 h-14 px-4 py-2"
                    >
                        {submitting ? 'Advancing...' : 'Next Rider'}
                    </button>
                    {error && (
                        <div className="mt-4 rounded-lg border border-destructive/50 bg-destructive/10 p-4">
                            <div className="flex items-center space-x-2">
                                <span className="text-destructive">⚠️</span>
                                <p className="text-sm text-destructive">{error}</p>
                            </div>
                        </div>
                    )}
                </>
            );
        }

        // Regular participant rendering
        return (
            <>
                <div className="flex items-center justify-center space-x-2 mb-4">
                    <div className="h-8 w-8 rounded-full bg-green-500/10 flex items-center justify-center">
                        <span className="text-green-600 text-lg">🏇</span>
                    </div>
                    <h4 className="text-xl font-bold text-foreground">Currently Running</h4>
                </div>
                <p className="text-2xl font-bold text-green-600 mb-2">{participant.name}</p>
                <p className="text-lg text-muted-foreground mb-2">{participant.horseName}</p>
                {participant.homeTown && (
                    <p className="text-lg text-muted-foreground">
                        From: {participant.homeTown}
                    </p>
                )}
            </>
        );
    };

    if (!eventStatus || !eventStatus.currentClass) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen p-4">
                <div className="text-lg text-muted-foreground">Loading presenter view...</div>
                {!wsConnected && (
                    <div className="text-sm text-red-500 mt-2">
                        Reconnecting to event...
                    </div>
                )}
            </div>
        );
    }

    const current = eventStatus.currentClass.participants[eventStatus.currentParticipantIndex] || null;
    const next = eventStatus.currentClass.participants[eventStatus.currentParticipantIndex + 1] || null;
    const inTheHole = eventStatus.currentClass.participants[eventStatus.currentParticipantIndex + 2] || null;

    return (
        <>
            <div className="flex flex-col items-center bg-background p-4">
                {/* Back to Producer View Button */}
                <div className="fixed top-4 left-4 z-10">
                    <Link
                        to={`/producer/event/${currentEventId}`}
                        className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-gray-600 text-white hover:bg-gray-700 h-10 px-4 py-2"
                    >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Back to Producer View
                    </Link>
                </div>

                {/* Live Status Indicator */}
                <div className="fixed top-4 right-4 flex items-center space-x-2 rounded-lg border bg-card px-3 py-1.5 shadow-sm">
                    <div className={`h-3 w-3 rounded-full ${wsConnected
                        ? 'bg-green-500'
                        : 'bg-red-500'
                        }`} />
                    <span className={`text-sm font-medium ${wsConnected
                        ? 'text-green-600'
                        : 'text-red-600'
                        }`}>
                        {wsConnected ? 'Live' : 'Connecting...'}
                    </span>
                </div>

                {/* Connection Warning */}
                {!wsConnected && (
                    <div className="w-full max-w-md mx-auto mb-4 rounded-lg border border-orange-500/50 bg-orange-500/10 p-4 text-center">
                        <div className="flex items-center justify-center space-x-2">
                            <span className="text-orange-600">⚠️</span>
                            <p className="text-sm text-orange-600">Reconnecting to event...</p>
                        </div>
                    </div>
                )}
            </div>
            <div className="container mx-auto max-w-4xl p-4 space-y-6">
                <div className="w-full max-w-md mx-auto space-y-6">
                    {/* Current Class Info */}
                    <div className="rounded-lg border bg-card p-6 shadow-sm">
                        <div className="flex items-center space-x-2 mb-4">
                            <div className="h-6 w-6 rounded-full bg-blue-500/10 flex items-center justify-center">
                                <span className="text-blue-600 text-sm">🏆</span>
                            </div>
                            <h2 className="text-lg font-semibold text-foreground">{eventStatus.currentClass.name}</h2>
                        </div>
                        <div className="space-y-2">
                            <p className="text-sm text-muted-foreground">
                                Rider {eventStatus.currentParticipantIndex + 1} of {eventStatus.currentClass.participants.length}
                            </p>
                            {/* Drag Countdown */}
                            {calculateRunsUntilDrag(eventStatus) !== null && (
                                <div className="flex items-center justify-between mt-2 pt-2 border-t">
                                    <div className="flex items-center space-x-2">
                                        <span className="text-gray-600">🚜</span>
                                        <span className="text-sm">Next Arena Drag</span>
                                    </div>
                                    <div className="flex items-center space-x-1">
                                        <span className="text-sm text-muted-foreground">in</span>
                                        <span className="font-bold text-lg">{calculateRunsUntilDrag(eventStatus)}</span>
                                        <span className="text-sm text-muted-foreground">runs</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Current Participant */}
                    <div className="rounded-lg border-2 border-green-500 bg-green-50 p-6 shadow-sm">
                        <div className="text-center">
                            {renderCurrentParticipant(eventStatus.currentParticipant)}
                        </div>
                    </div>

                    {/* Time Entry Form - Only show for regular participants */}
                    {current && !eventStatus.currentParticipant?.type && (
                        <div className="rounded-lg border bg-card p-6 shadow-sm">
                            <div className="flex items-center space-x-2 mb-4">
                                <div className="h-6 w-6 rounded-full bg-green-500/10 flex items-center justify-center">
                                    <span className="text-green-600 text-sm">⏱️</span>
                                </div>
                                <h4 className="text-lg font-semibold text-foreground">Enter Time</h4>
                            </div>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-foreground">Time (seconds)</label>
                                    <input
                                        type="number"
                                        step="0.001"
                                        min="0"
                                        placeholder="Enter time in seconds"
                                        value={inputTime}
                                        onChange={e => setInputTime(e.target.value)}
                                        className="flex h-14 w-full rounded-md border border-input bg-background px-3 py-2 text-2xl ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                        autoFocus
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-foreground">Penalties (optional)</label>
                                    <input
                                        type="number"
                                        step="0.001"
                                        min="0"
                                        placeholder="Enter penalties in seconds"
                                        value={inputPenalties}
                                        onChange={e => setInputPenalties(e.target.value)}
                                        className="flex h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-lg ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={submitting || !inputTime}
                                    className="inline-flex w-full items-center justify-center rounded-md text-lg font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-green-600 text-white hover:bg-green-700 h-14 px-4 py-2"
                                >
                                    {submitting ? 'Submitting...' : 'Submit Time'}
                                </button>
                                {error && (
                                    <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
                                        <div className="flex items-center space-x-2">
                                            <span className="text-destructive">⚠️</span>
                                            <p className="text-sm text-destructive">{error}</p>
                                        </div>
                                    </div>
                                )}
                            </form>
                        </div>
                    )}

                    {/* Next Up */}
                    <div className="rounded-lg border bg-card p-6 shadow-sm">
                        <div className="flex items-center space-x-2 mb-4">
                            <div className="h-6 w-6 rounded-full bg-purple-500/10 flex items-center justify-center">
                                <span className="text-purple-600 text-sm">👥</span>
                            </div>
                            <h4 className="text-lg font-semibold text-foreground">Next Up</h4>
                        </div>
                        {next ? (
                            <div className="space-y-1">
                                <p className="text-xl font-semibold text-foreground">{next.name}</p>
                                <p className="text-base text-muted-foreground">{next.horseName}</p>
                            </div>
                        ) : (
                            <p className="text-base text-muted-foreground">No one next</p>
                        )}
                    </div>

                    {/* In the Hole */}
                    <div className="rounded-lg border bg-card p-6 shadow-sm">
                        <div className="flex items-center space-x-2 mb-4">
                            <div className="h-6 w-6 rounded-full bg-purple-500/10 flex items-center justify-center">
                                <span className="text-purple-600 text-sm">👥</span>
                            </div>
                            <h4 className="text-lg font-semibold text-foreground">In The Hole</h4>
                        </div>
                        {inTheHole ? (
                            <div className="space-y-1">
                                <p className="text-xl font-semibold text-foreground">{inTheHole.name}</p>
                                <p className="text-base text-muted-foreground">{inTheHole.horseName}</p>
                            </div>
                        ) : (
                            <p className="text-base text-muted-foreground">No one in the hole</p>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
} 